import React, { useRef, useState } from 'react';
import { Popover, Tooltip, theme } from 'antd';
import { DndContext, PointerSensor, useDraggable, useSensor, useSensors } from '@dnd-kit/core';
import dayjs from 'libs/dayjs';
import {
  rangoDelDiagrama,
  barraDeTarea,
  barraDerivadaDelPadre,
  avisosDeTarea,
  mesesDelRango,
  finesDeSemana,
  columnaDeMarca,
  columnaInicial,
  estadoDeBarra,
  fechasRedimensionadas,
  fechasDeBarraFantasma,
} from './ganttGeometry';
import {
  COL_WIDTH,
  ROW_HEIGHT,
  HEADER_LEVEL_HEIGHT,
  HEADER_HEIGHT,
  LEFT_WIDTH,
} from './ganttLayout';
import GanttBarPopover from './GanttBarPopover';

export { COL_WIDTH, ROW_HEIGHT };

// Mitad derecha de la carta Gantt: las barras en el tiempo. Se dibuja a mano con
// un grid CSS de un día por columna (`colWidth` px) — sin librería de Gantt. El
// ancho de columna es un número conocido para que el arrastre traduzca píxeles →
// días de forma determinista (y testeable sin layout real).
//
// - La barra de una tarea con subtareas NO se edita: se deriva de las hijas.
// - Una barra editable se mueve arrastrándola, se redimensiona por sus dos bordes y, al
//   hacer clic, abre un popover con fechas y responsable.
// - Una tarea sin fechas muestra una barra fantasma: al arrastrarla (o al hacer clic y
//   elegir fechas) se le fijan las fechas.
// - Las flechas de dependencia se dibujan como líneas SVG sobre el grid.
// - Una barra con avisos (juicio del usuario) se pinta en el color de error.
// - Marcas: línea de hoy, línea de la fecha comprometida y fines de semana sombreados.
// - No tiene scroll propio: el contenedor (GanttChart) lo da, junto con la lista.
//
// Props:
//   tareas, dependencias, fechaComprometida — datos del summary.
//   especialistas — [{ id, fullname }] para el responsable del popover.
//   rango — { desde, hasta, dias }; si falta, se calcula con `hoy`.
//   hoy — 'YYYY-MM-DD' | null; sin él no hay línea de hoy.
//   colWidth — ancho de un día en px.
//   offsetIzquierdo — ancho de la lista fija a la izquierda; los rótulos pegados (mes, "sin fechas")
//     se anclan justo después. 0 si no hay lista (vista de celular).
//   readOnly — sin arrastre, redimensionado ni popover (ODT finalizada, vista de celular).
//   onCambiarFechas(idTarea, { planned_start, planned_end }) — al soltar o redimensionar una barra.
//   onEditarItem(idTarea, campos) — guardado del popover; devuelve la respuesta del API.

// Duración inicial (en días) de la barra fantasma.
const DIAS_FANTASMA = 3;
const ANCHO_MANIJA = 8;

// Envuelve una barra con su popover de edición (controlado desde GanttBars).
function ConPopover({ abierta, onAbrirCambio, contenido, children }) {
  if (!contenido) return children;
  return (
    <Popover
      open={abierta}
      onOpenChange={onAbrirCambio}
      trigger="click"
      placement="bottom"
      destroyTooltipOnHide
      content={contenido}
    >
      {children}
    </Popover>
  );
}

function BarraArrastrable({
  tarea,
  bar,
  estado,
  esPadre,
  avisos,
  readOnly,
  colors,
  colWidth,
  abierta,
  onAbrirCambio,
  contenidoPopover,
  onRedimensionar,
}) {
  const sinEdicion = readOnly || esPadre;
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `bar-${tarea.id}`,
    disabled: sinEdicion,
  });
  // Redimensionado en curso: { borde, delta } en días; solo para dibujar la vista previa.
  const [preview, setPreview] = useState(null);
  const ultimoDelta = useRef(0);

  let col = bar.col;
  let span = bar.span;
  if (preview) {
    if (preview.borde === 'inicio') {
      const mover = Math.min(Math.max(preview.delta, -bar.col), bar.span - 1);
      col = bar.col + mover;
      span = bar.span - mover;
    } else {
      span = Math.max(1, bar.span + preview.delta);
    }
  }

  const iniciarRedimension = (borde) => (e) => {
    // No dejar que el clic llegue al arrastre de la barra ni abra el popover.
    e.stopPropagation();
    e.preventDefault();
    const x0 = e.clientX;
    ultimoDelta.current = 0;

    const mover = (ev) => {
      const delta = Math.round((ev.clientX - x0) / colWidth);
      ultimoDelta.current = delta;
      setPreview({ borde, delta });
    };
    const soltar = () => {
      window.removeEventListener('pointermove', mover);
      window.removeEventListener('pointerup', soltar);
      setPreview(null);
      const fechas = fechasRedimensionadas(tarea, borde, ultimoDelta.current);
      if (fechas) onRedimensionar?.(tarea.id, fechas);
    };
    window.addEventListener('pointermove', mover);
    window.addEventListener('pointerup', soltar);
  };

  const conflicto = estado === 'aviso';
  const style = {
    gridColumn: `${col + 1} / span ${span}`,
    position: 'relative',
    height: 24,
    alignSelf: 'center',
    borderRadius: 6,
    background: conflicto ? colors.error : colors.primary,
    opacity: esPadre ? 0.6 : 1,
    outline: esPadre ? `1px dashed ${colors.primary}` : 'none',
    outlineOffset: 1,
    cursor: sinEdicion ? 'default' : 'grab',
    transform: transform ? `translateX(${transform.x}px)` : undefined,
    zIndex: isDragging || preview ? 10 : 2,
    display: 'flex',
    alignItems: 'center',
    paddingInline: 8,
    color: '#fff',
    fontSize: 12,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    touchAction: 'none',
  };

  // Barra angosta: el nombre se lee a la derecha, fuera de la barra.
  const etiquetaAfuera = span * colWidth < 110;

  const manija = (borde) => (
    <span
      data-testid={`gantt-manija-${borde}`}
      onPointerDown={iniciarRedimension(borde)}
      onClick={(e) => e.stopPropagation()}
      style={{
        position: 'absolute',
        top: 0,
        bottom: 0,
        [borde === 'inicio' ? 'left' : 'right']: 0,
        width: ANCHO_MANIJA,
        cursor: 'ew-resize',
        background: 'rgba(255,255,255,0.28)',
        touchAction: 'none',
      }}
    />
  );

  const barra = (
    <div
      ref={setNodeRef}
      data-testid="gantt-bar"
      data-tarea-id={tarea.id}
      data-conflicto={conflicto ? 'true' : 'false'}
      data-estado={estado}
      style={style}
      {...(sinEdicion ? {} : listeners)}
      {...attributes}
    >
      {sinEdicion ? null : manija('inicio')}
      {etiquetaAfuera ? null : tarea.description}
      {sinEdicion ? null : manija('fin')}
    </div>
  );

  const conPopover = (
    <ConPopover
      abierta={abierta}
      onAbrirCambio={onAbrirCambio}
      contenido={sinEdicion ? null : contenidoPopover}
    >
      {barra}
    </ConPopover>
  );

  return (
    <>
      {avisos.length > 0 ? (
        <Tooltip title={avisos.join(' · ')} color={colors.error} open={abierta ? false : undefined}>
          {conPopover}
        </Tooltip>
      ) : (
        conPopover
      )}
      {etiquetaAfuera ? (
        <div
          style={{
            gridColumn: `${col + span + 1} / span 10`,
            alignSelf: 'center',
            paddingLeft: 6,
            fontSize: 12,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            color: colors.text,
            pointerEvents: 'none',
          }}
        >
          {tarea.description}
        </div>
      ) : null}
    </>
  );
}

// Barra fantasma de una tarea sin fechas: se arrastra al lugar para fijarlas.
function BarraFantasma({ tarea, col, colors, abierta, onAbrirCambio, contenidoPopover }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `ghost-${tarea.id}`,
  });
  return (
    <ConPopover abierta={abierta} onAbrirCambio={onAbrirCambio} contenido={contenidoPopover}>
      <div
        ref={setNodeRef}
        data-testid="gantt-fantasma"
        data-tarea-id={tarea.id}
        style={{
          gridColumn: `${col + 1} / span ${DIAS_FANTASMA}`,
          alignSelf: 'center',
          height: 24,
          borderRadius: 6,
          border: `1px dashed ${colors.primary}`,
          background: 'transparent',
          color: colors.primary,
          opacity: isDragging ? 1 : 0.75,
          fontSize: 11,
          display: 'flex',
          alignItems: 'center',
          paddingInline: 6,
          whiteSpace: 'nowrap',
          cursor: 'grab',
          transform: transform ? `translateX(${transform.x}px)` : undefined,
          zIndex: isDragging ? 10 : 2,
          touchAction: 'none',
        }}
        {...listeners}
        {...attributes}
      >
        Fijar fechas
      </div>
    </ConPopover>
  );
}

// Rótulo pequeño de una marca vertical, pegado al borde inferior de la cabecera.
function MarcaCabecera({ testid, left, color, texto, alineadaALaIzquierda = false }) {
  return (
    <span
      data-testid={testid}
      style={{
        position: 'absolute',
        bottom: 0,
        left,
        transform: alineadaALaIzquierda ? 'translateX(-100%)' : 'translateX(-50%)',
        fontSize: 10,
        lineHeight: '14px',
        padding: '0 4px',
        borderRadius: 3,
        background: color,
        color: '#fff',
        whiteSpace: 'nowrap',
        zIndex: 5,
        pointerEvents: 'none',
      }}
    >
      {texto}
    </span>
  );
}

export default function GanttBars({
  tareas = [],
  dependencias = [],
  fechaComprometida = null,
  especialistas = [],
  rango: rangoProp = null,
  hoy = null,
  colWidth = COL_WIDTH,
  offsetIzquierdo = LEFT_WIDTH,
  readOnly = false,
  onCambiarFechas,
  onEditarItem,
}) {
  const { token } = theme.useToken();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
  // Id de la tarea cuyo popover está abierto (o null).
  const [abierta, setAbierta] = useState(null);

  const rango = rangoProp ?? rangoDelDiagrama(tareas, fechaComprometida, hoy);
  const dias = Number.isFinite(rango.dias) && rango.dias > 0 ? rango.dias : 1;
  // El popover necesita poder guardar; sin `onEditarItem` las barras solo se arrastran.
  const puedeEditarEnPopover = !readOnly && Boolean(onEditarItem);

  // Barra por tarea: derivada si tiene subtareas, directa si no.
  const barraPorTarea = new Map();
  tareas.forEach((t) => {
    const hijas = tareas.filter((h) => h.parent_id === t.id);
    const esPadre = hijas.length > 0;
    const bar = esPadre ? barraDerivadaDelPadre(hijas, rango.desde) : barraDeTarea(t, rango.desde);
    const avisos = avisosDeTarea(t, tareas, dependencias, fechaComprometida);
    barraPorTarea.set(t.id, {
      bar,
      esPadre,
      avisos,
      estado: estadoDeBarra(t, { esPadre, avisos, bar }),
    });
  });

  const filaDeTarea = new Map(tareas.map((t, i) => [t.id, i]));
  // Columna donde aparece la barra fantasma: hoy si está en el rango, si no el inicio.
  const colFantasma = columnaInicial({ hoy, desde: rango.desde, dias });

  const handleDragEnd = ({ active, delta }) => {
    if (readOnly || !onCambiarFechas || !delta?.x) return;
    const deltaDias = Math.round(delta.x / colWidth);
    if (deltaDias === 0) return;
    const [tipo, idTexto] = String(active.id).split('-');
    const id = Number(idTexto);
    const tarea = tareas.find((t) => t.id === id);
    if (!tarea) return;

    if (tipo === 'ghost') {
      const col = Math.min(Math.max(colFantasma + deltaDias, 0), dias - 1);
      onCambiarFechas(id, fechasDeBarraFantasma({ desde: rango.desde, col, dias: DIAS_FANTASMA }));
      return;
    }
    if (!tarea.planned_start || !tarea.planned_end) return;
    onCambiarFechas(id, {
      planned_start: dayjs(tarea.planned_start).add(deltaDias, 'day').format('YYYY-MM-DD'),
      planned_end: dayjs(tarea.planned_end).add(deltaDias, 'day').format('YYYY-MM-DD'),
    });
  };

  const contenidoPopover = (t) =>
    puedeEditarEnPopover ? (
      <GanttBarPopover
        tarea={t}
        especialistas={especialistas}
        onGuardar={onEditarItem}
        onCerrar={() => setAbierta(null)}
      />
    ) : null;
  const alCambiarApertura = (id) => (open) => setAbierta(open ? id : null);

  // Flechas de dependencia: del fin de la predecesora al inicio de la sucesora.
  const flechas = dependencias
    .map((d, i) => {
      const succ = barraPorTarea.get(d.id_item);
      const pred = barraPorTarea.get(d.id_item_predecesor);
      const filaSucc = filaDeTarea.get(d.id_item);
      const filaPred = filaDeTarea.get(d.id_item_predecesor);
      if (!succ?.bar || !pred?.bar || filaSucc == null || filaPred == null) return null;
      const x1 = (pred.bar.col + pred.bar.span) * colWidth;
      const y1 = filaPred * ROW_HEIGHT + ROW_HEIGHT / 2;
      const x2 = succ.bar.col * colWidth;
      const y2 = filaSucc * ROW_HEIGHT + ROW_HEIGHT / 2;
      return { key: `${d.id_item_predecesor}-${d.id_item}-${i}`, x1, y1, x2, y2 };
    })
    .filter(Boolean);

  const anchoTotal = dias * colWidth;
  const altoCuerpo = tareas.length * ROW_HEIGHT;
  const finesSemana = finesDeSemana(rango.desde, dias);
  const esFinDeSemana = new Set(finesSemana);
  const meses = mesesDelRango(rango.desde, dias);
  const colHoy = columnaDeMarca(hoy, rango.desde, dias);
  const colComprometida = columnaDeMarca(fechaComprometida, rango.desde, dias);
  // Con columnas angostas solo se rotula un día de cada siete.
  const rotularTodos = colWidth >= 24;

  const columnasStyle = {
    display: 'grid',
    gridTemplateColumns: `repeat(${dias}, ${colWidth}px)`,
  };
  const colors = {
    primary: token.colorPrimary,
    error: token.colorError,
    text: token.colorTextSecondary,
  };

  return (
    <div style={{ position: 'relative', width: anchoTotal, flex: 'none' }}>
      {/* Cabecera pegada arriba: mes y día */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 4,
          height: HEADER_HEIGHT,
          boxSizing: 'border-box',
          background: token.colorBgContainer,
          borderBottom: `1px solid ${token.colorBorderSecondary}`,
        }}
      >
        <div style={{ ...columnasStyle, height: HEADER_LEVEL_HEIGHT }}>
          {meses.map((m) => (
            <div
              key={`${m.label}-${m.col}`}
              style={{
                gridColumn: `${m.col + 1} / span ${m.span}`,
                borderLeft: `1px solid ${token.colorBorderSecondary}`,
                fontSize: 12,
                fontWeight: 600,
                color: token.colorText,
                lineHeight: `${HEADER_LEVEL_HEIGHT}px`,
                textTransform: 'capitalize',
                overflow: 'hidden',
              }}
            >
              {/* El rótulo del mes queda a la vista mientras el mes esté en pantalla */}
              <span style={{ position: 'sticky', left: offsetIzquierdo + 8, paddingLeft: 8 }}>
                {m.label}
              </span>
            </div>
          ))}
        </div>
        <div style={{ ...columnasStyle, height: HEADER_LEVEL_HEIGHT }}>
          {Array.from({ length: dias }, (_, i) => {
            const fecha = dayjs(rango.desde).add(i, 'day');
            const finde = esFinDeSemana.has(i);
            return (
              <div
                key={i}
                style={{
                  fontSize: 11,
                  textAlign: 'center',
                  lineHeight: `${HEADER_LEVEL_HEIGHT}px`,
                  borderLeft: `1px solid ${token.colorFillQuaternary}`,
                  background: finde ? token.colorFillQuaternary : undefined,
                  color: finde ? token.colorTextQuaternary : token.colorTextTertiary,
                  overflow: 'hidden',
                }}
              >
                {rotularTodos || i % 7 === 0 ? fecha.format('DD') : ''}
              </div>
            );
          })}
        </div>
        {colHoy != null ? (
          <MarcaCabecera
            testid="gantt-hoy-rotulo"
            left={colHoy * colWidth + colWidth / 2}
            color={token.colorPrimary}
            texto="Hoy"
          />
        ) : null}
        {colComprometida != null ? (
          <MarcaCabecera
            testid="gantt-comprometida-rotulo"
            left={(colComprometida + 1) * colWidth}
            color={token.colorError}
            texto="Comprometida"
            alineadaALaIzquierda
          />
        ) : null}
      </div>

      {/* Cuerpo: fines de semana, filas con barras, marcas y flechas */}
      <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
        <div style={{ position: 'relative', height: altoCuerpo }}>
          {finesSemana.map((col) => (
            <div
              key={`fs-${col}`}
              style={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: col * colWidth,
                width: colWidth,
                background: token.colorFillQuaternary,
                opacity: 0.6,
                pointerEvents: 'none',
              }}
            />
          ))}

          {tareas.map((t, i) => {
            const { bar, esPadre, avisos, estado } = barraPorTarea.get(t.id);
            const fantasma = !bar && !esPadre && !readOnly;
            return (
              <div
                key={t.id}
                style={{
                  ...columnasStyle,
                  position: 'absolute',
                  top: i * ROW_HEIGHT,
                  left: 0,
                  width: anchoTotal,
                  height: ROW_HEIGHT,
                  boxSizing: 'border-box',
                  borderBottom: `1px solid ${token.colorFillQuaternary}`,
                }}
              >
                {bar ? (
                  <BarraArrastrable
                    tarea={t}
                    bar={bar}
                    estado={estado}
                    esPadre={esPadre}
                    avisos={avisos}
                    readOnly={readOnly}
                    colors={colors}
                    colWidth={colWidth}
                    abierta={abierta === t.id}
                    onAbrirCambio={alCambiarApertura(t.id)}
                    contenidoPopover={contenidoPopover(t)}
                    onRedimensionar={onCambiarFechas}
                  />
                ) : fantasma ? (
                  <BarraFantasma
                    tarea={t}
                    col={colFantasma}
                    colors={colors}
                    abierta={abierta === t.id}
                    onAbrirCambio={alCambiarApertura(t.id)}
                    contenidoPopover={contenidoPopover(t)}
                  />
                ) : (
                  <div
                    data-testid="gantt-sin-fechas"
                    style={{
                      gridColumn: `1 / span ${Math.min(dias, 14)}`,
                      alignSelf: 'center',
                      position: 'sticky',
                      left: offsetIzquierdo + 8,
                      fontSize: 12,
                      fontStyle: 'italic',
                      color: avisos.length ? token.colorError : token.colorTextTertiary,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {avisos[0] ?? 'Sin fechas'}
                  </div>
                )}
              </div>
            );
          })}

          {colHoy != null ? (
            <div
              data-testid="gantt-hoy"
              style={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: colHoy * colWidth + colWidth / 2 - 1,
                width: 2,
                background: token.colorPrimary,
                opacity: 0.7,
                pointerEvents: 'none',
                zIndex: 3,
              }}
            />
          ) : null}
          {colComprometida != null ? (
            <div
              data-testid="gantt-comprometida"
              style={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: (colComprometida + 1) * colWidth - 1,
                borderLeft: `2px dashed ${token.colorError}`,
                opacity: 0.8,
                pointerEvents: 'none',
                zIndex: 3,
              }}
            />
          ) : null}

          {/* Flechas de dependencia por encima del grid */}
          {flechas.length > 0 ? (
            <svg
              width={anchoTotal}
              height={altoCuerpo}
              style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none', zIndex: 3 }}
            >
              <defs>
                <marker
                  id="gantt-arrow"
                  markerWidth="6"
                  markerHeight="6"
                  refX="5"
                  refY="3"
                  orient="auto"
                >
                  <path d="M0,0 L6,3 L0,6 Z" fill={token.colorTextTertiary} />
                </marker>
              </defs>
              {flechas.map((f) => (
                <path
                  key={f.key}
                  data-testid="gantt-arrow"
                  d={`M ${f.x1} ${f.y1} C ${f.x1 + 16} ${f.y1}, ${f.x2 - 16} ${f.y2}, ${f.x2} ${
                    f.y2
                  }`}
                  stroke={token.colorTextTertiary}
                  strokeWidth="1.5"
                  fill="none"
                  markerEnd="url(#gantt-arrow)"
                />
              ))}
            </svg>
          ) : null}
        </div>
      </DndContext>
    </div>
  );
}
